import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  getClustersList,
  getClustersListError,
  getClustersListResponse,
  loadMoreClustersList,
  loadMoreClustersListResponse,
  removeCluster,
  removeClusterError,
  removeClusterItem,
  removeClusterResponse,
  saveCluster,
  saveClusterError,
  saveClusterResponse,
  searchClustersList,
  searchClustersListResponse,
  updateCluster,
  updateClusterError,
  updateClusterResponse,
} from './clusters.actions';
import { ClustersResource } from '../resources/clusters.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { EVENTS } from '../../constants/events';
import { Lazy } from '../../lazy';
import { NotifyService } from '../../common/services/notify.service';

@Injectable()
export class ClustersEffects {
  @Effect()
  getClusters$ = this.actions$.pipe(
    ofType(getClustersList),
    switchMap(({ params }) =>
      this.clustersResource.query(params).pipe(
        map((clusters) => getClustersListResponse({ clusters })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getClustersListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  searchClusters$ = this.actions$.pipe(
    ofType(searchClustersList),
    switchMap(({ params }) =>
      this.clustersResource.search(params).pipe(
        map((clusters) => searchClustersListResponse({ clusters })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getClustersListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreClusters$ = this.actions$.pipe(
    ofType(loadMoreClustersList),
    switchMap(({ params }) =>
      (params.q ? this.clustersResource.search(params) : this.clustersResource.query(params)).pipe(
        map((clusters) => loadMoreClustersListResponse({ clusters })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getClustersListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  saveCluster$ = this.actions$.pipe(
    ofType(saveCluster),
    switchMap(({ cluster }) =>
      this.clustersResource.save(cluster, { include: 'packages' } as any).pipe(
        map((clusterResponse) => {
          this.$rootScope.$broadcast(EVENTS.CLUSTER_CREATED, clusterResponse);
          return saveClusterResponse({ data: clusterResponse });
        }),
        catchError((response) => of(saveClusterError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updateCluster$ = this.actions$.pipe(
    ofType(updateCluster),
    switchMap(({ cluster, params, clusterId }) =>
      this.clustersResource.update(clusterId, cluster, { ...params, include: 'packages' } as any).pipe(
        map((clusterResponse) => updateClusterResponse({ data: clusterResponse })),
        catchError((response) => of(updateClusterError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeCluster$ = this.actions$.pipe(
    ofType(removeCluster),
    switchMap(({ clusterId }) =>
      this.clustersResource.remove(clusterId).pipe(
        map((clusterResponse) => removeClusterItem({ data: clusterResponse })),
        catchError((response) => of(removeClusterError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeClusterResponse$ = this.actions$.pipe(
    ofType(removeClusterItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeClusterResponse({ data })),
  );

  private _$rootScope = new Lazy<any>(() => this.injector.get('$rootScope'));

  constructor(
    private actions$: Actions,
    private clustersResource: ClustersResource,
    private readonly injector: Injector,
    private notify: NotifyService,
    private translate: TranslateService,
  ) {}

  private get $rootScope() {
    // eslint-disable-next-line no-underscore-dangle
    return this._$rootScope.value;
  }
}
